import type { ComponentType } from 'react';
import { matchesNavigationPath } from '@/utils/navigationPath';

type Icon = ComponentType<{ className?: string }>;
export interface NavigationLeaf { icon: Icon; labelKey: string; href: string }
export interface NavigationItem { icon: Icon; labelKey: string; href?: string; children?: NavigationLeaf[] }
type Section = { href: string } | { labelKey: string; paths: string[]; iconPath?: string; keepSingle?: boolean };

/** Organize already-authorized links; never create or authorize a route here. */
function sections(items: NavigationItem[], structure: Section[]): NavigationItem[] {
  const leaves: NavigationLeaf[] = items.flatMap(item => item.children ?? (item.href ? [{ icon: item.icon, labelKey: item.labelKey, href: item.href }] : []));
  const byPath = new Map(leaves.map(item => [item.href, item]));
  const used = new Set<string>();
  const result: NavigationItem[] = [];
  for (const section of structure) {
    if ('href' in section) {
      const leaf = byPath.get(section.href);
      if (leaf && !used.has(leaf.href)) { result.push(leaf); used.add(leaf.href); }
      continue;
    }
    const children = section.paths.flatMap(href => {
      const leaf = byPath.get(href);
      if (!leaf || used.has(href)) return [];
      used.add(href); return [leaf];
    });
    if (!children.length) continue;
    if (children.length === 1 && !section.keepSingle) result.push(children[0]);
    else result.push({ icon: byPath.get(section.iconPath ?? '')?.icon ?? children[0].icon, labelKey: section.labelKey, children });
  }
  // New authorized routes remain discoverable until assigned a section.
  for (const leaf of leaves) if (!used.has(leaf.href)) { result.push(leaf); used.add(leaf.href); }
  return result;
}

const key = (name: string) => 'layout.sidebar.navigationGroups.' + name;

export function groupProviderSidebar(items: NavigationItem[], bookingOnly: boolean): NavigationItem[] {
  const has = (href: string) => items.some(item => item.href === href);
  const orderLabel = bookingOnly ? key('appointments')
    : has('/business/shop-orders') && (has('/business/calendar') || has('/business/visit-schedule')) ? key('ordersScheduling')
    : has('/business/visit-schedule') ? key('visits')
    : has('/business/calendar') ? key('appointments') : key('orders');
  return sections(items, [
    { href: '/business' },
    { labelKey: orderLabel, paths: ['/business/orders', '/business/shop-orders', '/business/deliveries', '/business/returns', '/business/calendar', '/business/visit-schedule'] },
    { labelKey: key('catalog'), paths: ['/business/services', '/business/products', '/business/catalog-imports'] },
    { labelKey: key('inventory'), paths: ['/business/inventory', '/business/warehouses', '/business/stocktakes', '/business/replenishment'] },
    { labelKey: key('suppliers'), paths: ['/business/purchasing', '/business/payables', '/business/supplier-credits', '/business/supplier-stock-returns', '/business/supplier-advances'] },
    { labelKey: 'providerAdmin.operations', paths: ['/business/operations', '/business/map', '/business/zones', '/business/vehicles'] },
    { labelKey: key('team'), paths: ['/business/team', '/business/staff-access'] },
    { labelKey: key('sales'), paths: ['/business/customers', '/business/trade', '/business/promotions'] },
    { labelKey: 'providerAdmin.finance', paths: ['/business/finance', '/business/wallets', '/business/bank-reconciliation', '/business/cash-reconciliation'] },
    { labelKey: 'providerAdmin.reports', paths: ['/business/reports', '/business/scorecards'] },
    { href: '/business/settings' },
  ]);
}

export function groupTenantSidebar(items: NavigationItem[]): NavigationItem[] {
  return sections(items, [
    { href: '/' },
    { labelKey: 'providerAdmin.operations', paths: ['/orders', '/shop-orders', '/dispatchers', '/maps/orders', '/maps/providers', '/maps/nearby'] },
    { labelKey: 'layout.sidebar.people', paths: ['/customers', '/providers', '/providers/approval', '/providers/status'] },
    { labelKey: 'layout.sidebar.catalogManagement', paths: ['/categories', '/services', '/products'] },
    { labelKey: 'layout.sidebar.finance', paths: ['/transactions', '/providers/debts', '/settings/tax'], keepSingle: true },
    { labelKey: 'layout.sidebar.marketing', paths: ['/coupons', '/offers', '/notifications'] },
    { labelKey: key('supportFeedback'), paths: ['/tickets', '/support-hours', '/faqs', '/reviews'] },
    { labelKey: key('websiteApps'), paths: ['/branding', '/branding/landing-page', '/branding/sliders', '/app-versions', '/mobile-releases', '/settings/google-play'] },
    { labelKey: 'layout.sidebar.system', iconPath: '/settings', paths: ['/launch', '/config', '/settings', '/users-security', '/recurring-jobs', '/notification-settings', '/notification-logs'] },
  ]);
}

export function groupPlatformSidebar(items: NavigationItem[]): NavigationItem[] {
  return sections(items, [
    { href: '/' }, { href: '/tenants' }, { href: '/analytics' },
    { labelKey: 'layout.sidebar.platformData', paths: ['/countries', '/cities', '/nationalities'] },
    { labelKey: key('platformAdministration'), iconPath: '/platform-settings', paths: ['/platform-settings', '/notification-settings', '/recurring-jobs', '/platform/firebase'] },
  ]);
}

/** Nested detail paths select the most specific authorized destination only. */
export function activeNavigationHref(items: NavigationItem[], pathname: string): string | undefined {
  const paths = items.flatMap(item => item.children?.map(child => child.href) ?? (item.href ? [item.href] : []));
  return paths.filter(href => matchesNavigationPath(pathname, href)).sort((a, b) => b.length - a.length)[0];
}
